
import flowchart from "flowchart.js";
import hljs from 'highlight.js';
import MarkdownToJsx from 'markdown-to-jsx';
import mermaid from 'mermaid';
import React, { HTMLAttributes, PropsWithChildren } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
// @ts-ignore
import useExists from '@components/Hooks/useExists';
import plsql from './languages/plsql';
// @ts-ignore
hljs.registerLanguage('plsql', plsql);

type Props = HTMLAttributes<HTMLDivElement> & PropsWithChildren & {
  id: string,
};

const Markdown: React.FC<Props> = ({ ...props }) => {

  let mermaidId = 0;
  const mounted = useExists();

  const renderMermaid = (text: string): JSX.Element => {
    try {
      mermaidId++;
      return (<div dangerouslySetInnerHTML={{ __html: mermaid.mermaidAPI.render(props.id + '-mermaid-' + mermaidId, text) }} />);
    } catch (_err) {
      return (<span />);
    }
  }

  const renderFlowchart = (text: string): JSX.Element => {

    try {
      mermaidId++;
      const x = flowchart.parse(text);

      const x2 = (inst: flowchart.Instance, id: number) => {
        setTimeout(() => {
          if (!mounted.current) {
            return;
          }
          const div: HTMLElement | null = document.getElementById(props.id + '-xyz-' + id);
          if (div) {
            div.innerHTML = '';
          }
          inst.drawSVG(props.id + '-xyz-' + id, {
            'x': 0,
            'y': 0,
            'line-width': 2,
            'line-length': 40,
            'text-margin': 10,
            'font-size': 14,
            'font-family': 'Open Sans',
            'font-color': 'black',
            'line-color': 'black',
            'element-color': 'black',
            'fill': 'white',
            'yes-text': 'yes',
            'no-text': 'no',
            'arrow-end': 'block',
            'scale': 1,
            // even flowstate support ;-)
            'flowstate': {
              'success': {
                fill: '#dff0d8',
                'element-color': '#3c763d',
                'font-color': '#3c763d'
              },
              'warning': {
                fill: '#fcf8e3',
                'element-color': '#8a6d3b',
                'font-color': '#8a6d3b'
              },
              'danger': {
                fill: '#f2dede',
                'element-color': '#a94442',
                'font-color': '#a94442'
              },
              'info': {
                fill: '#d9edf7',
                'element-color': '#31708f',
                'font-color': '#31708f'
              },
              'default': {
                fill: '#cccccc',
                'element-color': '#555555',
                'font-color': '#555555'
              },
              'primary': {
                fill: '#428bca',
                'element-color': '#23527c',
                'font-color': '#ffffff'
              }
            }
          });
        }, 1);
      };
      if (x) {
        x2(x, mermaidId);
      }
      return (<div id={props.id + '-xyz-' + mermaidId} />);
    } catch (_err) {
      return (<span />);
    }
  }

  const languages: { [lang: string]: (text: string) => JSX.Element } = {
    'mermaid': renderMermaid,
    'flowchart': renderFlowchart
  }

  const CodeBlock = ({ className, children }: any) => {
    let lang = 'plaintext'; // default monospaced text
    if (className && className.startsWith('lang-')) {
      lang = className.replace('lang-', '');
    }
    if (languages[lang]) {
      return languages[lang](children);
    }
    try {
      return (<div dangerouslySetInnerHTML={{ __html: '<pre><code class="language-' + lang + '">' + hljs.highlight(lang, children).value + '</code></pre>' }} />);
    } catch (_err) {
      return (<span />);
    }

  }

  const PreBlock = ({ children, ...rest }: any): JSX.Element => {
    // if (detectIe() && detectIe() <= 11) {
    //   return <pre {...rest}>{children}</pre>;
    // }
    if ('type' in children && children.type === 'code') {
      return CodeBlock(children.props);
    }
    return <pre {...rest}>{children}</pre>;
  };

  const ErrorFallback = ({ error }: FallbackProps) => {
    return (
      <div role="alert">
        <p>Something went wrong:</p>
        <pre>{error.message}</pre>
        {/* <button onClick={resetErrorBoundary}>Try again</button> */}
      </div>
    )
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      resetKeys={[props.children]}
    >
      <MarkdownToJsx
        {...props}
        options={{
          namedCodesToUnicode: {
            frasl: '\u2044',
            tilde: '\u007E',
            null: ''
          },
          overrides: {
            pre: PreBlock,
          },
        }}
      >
        {props.children as string}
      </MarkdownToJsx>
    </ErrorBoundary>
  );

}

export default Markdown;